/*
  Size guide fallback desde Excel (Shopify > Contenido > Archivos)

  Se activa SOLO en productos que NO tengan asignado el metaobject
  product.metafields.laagam.size_guide. Los productos que ya lo tienen
  siguen renderizando con Liquid como hasta ahora.

  Workflow:
    1) Sube tu Excel a Shopify > Contenido > Archivos
    2) Copia la URL CDN que te da Shopify (boton "Copiar enlace")
    3) Pegala en: Personalizar tema > Configuracion del tema > Size guide
    4) Para actualizar medidas: edita el Excel y *reemplaza* el archivo
       en Shopify (la URL se mantiene si reemplazas, no si subes uno nuevo)

  Formato esperado del Excel:
    Bloques separados por filas en blanco. Cada bloque:
      Fila A: nombre del modelo (informativo, no se usa)
      Fila B: SKU base (ej: TP/M2009 o AFW2301)            <- obligatorio
      [Fila opcional]: IMAGE | https://cdn.shopify.com/.../foto.png
      Fila C: MEDIDAS | TALLA XS | TALLA S | TALLA M | TALLA L | TALLA XL
      Fila D+: nombre de medida | 37CM | 38CM | ... (admite "37,5CM")

  La fila IMAGE es opcional y puede ir en cualquier sitio del bloque.
  Si esta presente, aparece el bloque "How to measure" con la imagen
  + la lista numerada de medidas. Si no, solo la tabla.
*/
(function () {
  'use strict';

  const XLSX_URL = (window.SGD_XLSX_URL || '').trim();
  const SHEETJS_CDN = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';

  let dataPromise = null;

  function loadSheetJS() {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    return new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = SHEETJS_CDN;
      s.onload = function () { resolve(window.XLSX); };
      s.onerror = function () { reject(new Error('Failed to load SheetJS')); };
      document.head.appendChild(s);
    });
  }

  function loadGuides() {
    if (dataPromise) return dataPromise;
    if (!XLSX_URL) {
      console.warn('[size-guide-from-xlsx] URL no configurada. Pegala en Personalizar tema > Configuracion del tema > Size guide.');
      return Promise.resolve(null);
    }
    dataPromise = (async function () {
      const XLSX = await loadSheetJS();
      const res = await fetch(XLSX_URL);
      if (!res.ok) throw new Error('xlsx fetch failed: ' + res.status);
      const buf = await res.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      return parseGuides(rows);
    })();
    return dataPromise;
  }

  const IMAGE_LABEL_RE = /^(IMAGEN|IMAGE|IMG)$/i;
  const HEADER_LABEL_RE = /^(MEDIDAS|MEASUREMENTS|MEASURES|SIZES?)$/i;

  function parseGuides(rows) {
    const guides = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] || [];
      const cell = String(r[0] == null ? '' : r[0]).trim();
      if (!HEADER_LABEL_RE.test(cell)) continue;

      let skuRow = -1;
      let imageUrl = null;
      for (let j = i - 1; j >= Math.max(0, i - 6); j--) {
        const v = String((rows[j] || [])[0] == null ? '' : (rows[j] || [])[0]).trim();
        if (!v) continue;
        if (IMAGE_LABEL_RE.test(v)) {
          const url = String((rows[j] || [])[1] == null ? '' : (rows[j] || [])[1]).trim();
          if (url) imageUrl = url;
          continue;
        }
        skuRow = j;
        break;
      }
      if (skuRow < 0) continue;

      const sku = String((rows[skuRow] || [])[0] == null ? '' : (rows[skuRow] || [])[0]).trim().toUpperCase();
      let name = '';
      for (let j = skuRow - 1; j >= Math.max(0, skuRow - 4); j--) {
        const v = String((rows[j] || [])[0] == null ? '' : (rows[j] || [])[0]).trim();
        if (!v) continue;
        if (IMAGE_LABEL_RE.test(v)) continue;
        name = v;
        break;
      }

      const header = rows[i] || [];
      const sizes = [];
      for (let c = 1; c < header.length; c++) {
        const h = String(header[c] == null ? '' : header[c]).trim();
        if (h) sizes.push(h);
      }
      if (!sizes.length) continue;

      const measurements = [];
      for (let j = i + 1; j < rows.length; j++) {
        const mr = rows[j] || [];
        const label = String(mr[0] == null ? '' : mr[0]).trim();
        if (!label) break;
        if (label.indexOf('**') === 0) continue;
        if (IMAGE_LABEL_RE.test(label)) {
          const url = String(mr[1] == null ? '' : mr[1]).trim();
          if (url) imageUrl = url;
          continue;
        }
        const values = [];
        for (let c = 1; c <= sizes.length; c++) {
          const raw = String(mr[c] == null ? '' : mr[c]).trim();
          const clean = raw.replace(/\s*CM\s*$/i, '').replace(',', '.').trim();
          values.push(clean);
        }
        const description = String(mr[sizes.length + 1] == null ? '' : mr[sizes.length + 1]).trim();
        measurements.push({ name: label, values: values, description: description });
      }
      if (measurements.length) {
        guides[sku] = { name: name, sizes: sizes, measurements: measurements, imageUrl: imageUrl };
      }
    }
    return guides;
  }

  function findGuide(productSku, guides) {
    if (!productSku) return null;
    const sku = String(productSku).toUpperCase().trim();
    const keys = Object.keys(guides).sort(function (a, b) { return b.length - a.length; });
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (sku === k || sku.indexOf(k + '/') === 0 || sku.indexOf(k + '-') === 0) {
        return guides[k];
      }
    }
    return null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function renderTable(guide) {
    let html = '<table><tr><th></th>';
    for (let i = 0; i < guide.measurements.length; i++) {
      html += '<th>' + escapeHtml(guide.measurements[i].name) + '</th>';
    }
    html += '</tr>';
    for (let i = 0; i < guide.sizes.length; i++) {
      html += '<tr><td><strong>' + escapeHtml(guide.sizes[i]) + '</strong></td>';
      for (let j = 0; j < guide.measurements.length; j++) {
        const v = guide.measurements[j].values[i] || '';
        html += '<td class="sgd-table-cm" data-cm="' + escapeHtml(v) + '">' + escapeHtml(v) + '</td>';
      }
      html += '</tr>';
    }
    html += '</table>';
    return html;
  }

  function renderHowToMeasure(guide) {
    if (!guide.imageUrl) return '';
    let html = '<div class="sgd-how-to-measure-text">How to measure </div>';
    html += '<div class="sgd-how-to-measure-img">';
    html += '<img src="' + escapeHtml(guide.imageUrl) + '" alt="" width="1000" height="auto" loading="lazy">';
    html += '</div>';
    html += '<div class="how-to-measure-descriptions">';
    for (let i = 0; i < guide.measurements.length; i++) {
      const m = guide.measurements[i];
      html += '<div class="how-to-measure-description">';
      html += '<div class="how-to-measure-description-title">' + (i + 1) + '. ' + escapeHtml(m.name) + '</div>';
      if (m.description) {
        html += '<div class="how-to-measure-description-text">' + escapeHtml(m.description) + '</div>';
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  async function fillFallback(container) {
    if (container.dataset.sgdFilled) return;
    container.dataset.sgdFilled = '1';

    let guides;
    try {
      guides = await loadGuides();
    } catch (e) {
      console.warn('[size-guide-from-xlsx]', e);
      return;
    }
    if (!guides) return;

    const sku = container.dataset.productSku;
    const guide = findGuide(sku, guides);
    if (!guide) return;

    container.dataset.columns = String(guide.measurements.length);
    container.innerHTML = renderTable(guide);

    const root = container.closest('.size-guide-drawer-inner') || document;

    const howTo = root.querySelector('[data-sgd-how-to]');
    if (howTo && guide.imageUrl) {
      howTo.innerHTML = renderHowToMeasure(guide);
      howTo.removeAttribute('hidden');
    }

    const bodyTabs = root.querySelectorAll('.sgd-tab[data-target="body"]');
    const convTabs = root.querySelectorAll('.sgd-tab[data-target="measurements"]');
    bodyTabs.forEach(function (el) { el.classList.add('active'); });
    convTabs.forEach(function (el) { el.classList.remove('active'); });

    const bodyBtn = root.querySelector('[data-sgd-body-btn]');
    const convBtn = root.querySelector('.sdq-tab-btn[data-target="measurements"]');
    if (bodyBtn) {
      bodyBtn.removeAttribute('hidden');
      bodyBtn.classList.add('active');
    }
    if (convBtn) convBtn.classList.remove('active');

    const toggle = document.querySelector('input[name="size-guide-table-toggle-size-type"]');
    if (toggle) toggle.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function init() {
    const containers = document.querySelectorAll('[data-sgd-fallback]');
    containers.forEach(function (container) {
      const drawer = container.closest('.size-guide-drawer');
      const details = container.closest('details');
      const trigger = function () { fillFallback(container); };

      if (drawer) {
        const obs = new MutationObserver(function (muts) {
          for (let i = 0; i < muts.length; i++) {
            if (muts[i].attributeName === 'class' && drawer.classList.contains('active')) {
              trigger();
            }
          }
        });
        obs.observe(drawer, { attributes: true });
        if (drawer.classList.contains('active')) trigger();
      }
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) trigger();
        });
        if (details.open) trigger();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
